import React, {useEffect, useState, useRef} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Form, Input, Tooltip, message, Select} from "antd";
import {BankOutlined, InfoCircleOutlined} from "@ant-design/icons";
import MaskedInput from "antd-mask-input";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as Sentry from "@sentry/react";

import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {addSubCompanyPropType} from "../addSubCompany/addSubCompanyPropTypes";
import {addSubCompanyValidation} from "../addSubCompany/addSubCompanyValidation";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {useSubCompanyLocationSync} from "../../../hooks/useSubCompanyLocationSync";
import TagInput from "../../_defaultComponents/TagInput/TagInput";
import {Switch} from "antd";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import DistrictFilter from "../../_defaultComponents/FilterComponents/DistrictFilter";
import DynamicNeighborhoodFilter from "../../_defaultComponents/FilterComponents/DynamicNeighborhoodFilter";
import {LocationFieldsHint} from "../updateSubCompany/UpdateSubCompanyStyles";
import {buildSubCompanyCreatePayload} from "../subCompanyPayload";
import {CreditModeSelect} from "../../_defaultComponents/CreditModeSelect";
import {
    invoiceCutPeriodOptions,
    isInvoicePeriodDisabled,
    isInvoicePeriodRequired,
    managerCompanyOptions
} from "../../../constants/constants";

export const AddSubCompany = ({companyId}) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation();
    const [companyCode, setCompanyCode] = useState();
    const [companyCityCode, setCompanyCityCode] = useState();
    const [isTestUser, setIsTestUser] = useState(false);
    const [jokerLeadIsActive, setJokerLeadIsActive] = useState(false);
    const [jokerLeadThreshold, setJokerLeadThreshold] = useState(0);
    const [tags, setTags] = useState([]);
    const [districtId, setDistrictId] = useState(null);
    const [preferGeocodingForLocation, setPreferGeocodingForLocation] = useState(false);
    const {
        resolveAddressFromLocation,
        resolveDistrictId,
        resolveLocationFromAddress,
        isResolving
    } = useSubCompanyLocationSync();
    // Forward geocode YALNIZCA kullanıcı il/ilçe/mahalle'yi elle seçtiğinde çalışsın diye
    // açık bir "izin" bayrağı kullanılıyor (varsayılan false). Böylece hem form ilk
    // yüklenirken/mevcut değerlerle yeniden başlatılırken hem de haritadan gelen
    // programatik doldurma sırasında efekt sessizce atlanır; yalnız CityFilter/
    // DistrictFilter/DynamicNeighborhoodFilter onChange'leri bayrağı true yapar.
    const allowForwardGeocodeRef = useRef(false);
    const isManualAddressRef = useRef(false);
    const [previewLocation, setPreviewLocation] = useState(null);
    const [isLocationPinned, setIsLocationPinned] = useState(false);

    /*TODO: Form component will be used in UpdateSubCompany and AddSubCompany pages. 
    Code refactoring needed for blocking duplicate usage.
    */

    const formik = useFormik({
        initialValues: {
            address: "",
            authorizedEmail: "",
            authorizedPhone: "",
            authorizedPerson: "",
            disabled: false,
            name: "",
            location: {
                longitude: 29.14,
                latitude: 41.09
            },
            uniqueKey: "",
            creditMode: undefined,
            invoicePeriod: undefined,
            managerCompany: undefined,
            subCompanyCode: "",
            city: "",
            county: "",
            district: "",
            companyId
        },
        validationSchema: addSubCompanyValidation,
        enableReinitialize: true,
        onSubmit: async (values: addSubCompanyPropType) => {
            values.authorizedPhone = values.authorizedPhone.replace(/[-,(,)]/g, "");
            values.subCompanyCode = `${companyCode}-${("00" + companyCityCode).slice(-2)}-${
                values.subCompanyCode
            }`;
            values.isTestUser = isTestUser;
            if (!values.invoicePeriod || isInvoicePeriodDisabled(values.creditMode))
                delete values.invoicePeriod;
            values.options = {
                ...(values.options || {}),
                jokerLeadOrder: {
                    isActive: jokerLeadIsActive,
                    thresholdMinutes: Number(jokerLeadThreshold) || 0
                }
            };
            await handleCompanySubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        setValues,
        submitCount
    } = formik;

    const handleCreditModeChange = value => {
        setValues({
            ...values,
            creditMode: value,
            ...(isInvoicePeriodDisabled(value) && {invoicePeriod: null})
        });
    };

    const handleCompanySubmit = async (values: addSubCompanyPropType) => {
        const payload = buildSubCompanyCreatePayload(values, {preferGeocodingForLocation});
        const result = await stores.companyStore.createSubCompany(payload);
        if (result) {
            history.push("/companies/" + companyId);
        }
    };

    useEffect(() => {
        stores.companyStore
            .generateSubcompanyCode({
                companyId,
                subCompanyLocation: values.location
            })
            .then(res => {
                setCompanyCode(res.companyCode);
                setCompanyCityCode(res.cityCode);
                setFieldValue("subCompanyCode", ("0000" + res.code).slice(-4));
            })
            .catch(e => Sentry.captureException(e));
    }, [values.location.longitude, values.location.latitude]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    // İl/ilçe/mahalle elle seçildiğinde marker'ı en spesifik seçime taşır — yalnız il
    // seçildiğinde il merkezine, ilçe de seçilince ilçe merkezine, mahalle de seçilince
    // mahalle merkezine (her adımda elimizdeki en dar bilgiyle geocode edilir). Yalnızca
    // kullanıcı Filter component'lerinden birini değiştirdiğinde (allowForwardGeocodeRef)
    // tetiklenir — form ilk yüklenirken/yeniden başlatılırken veya haritadan gelen
    // programatik doldurmada sessizce atlanır.
    useEffect(() => {
        if (!allowForwardGeocodeRef.current) return;
        allowForwardGeocodeRef.current = false;
        if (!values.city) return;

        const timer = setTimeout(async () => {
            const location = await resolveLocationFromAddress({
                city: values.city,
                county: values.county,
                district: values.district
            });
            if (!location) {
                message.warning("Seçilen konum için harita güncellenemedi.");
                return;
            }
            setPreviewLocation({
                lat: location.latitude,
                lng: location.longitude,
                zoom: values.district ? 15 : values.county ? 13 : 11
            });
        }, 800);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.city, values.county, values.district]);

    // Google linki (veya elle seçim yokken harita tıklaması): konum ve il/ilçe/mahalle
    // baştan OSM'den doldurulur.
    const handleLocationFromLink = async ({lat, lng}) => {
        isManualAddressRef.current = false;
        setPreviewLocation(null);
        setIsLocationPinned(true);
        // Harita tıklaması zaten konumun kaynağı; forward geocode
        // efektinin bu programatik doldurmayı tekrar işlemesini önle.
        allowForwardGeocodeRef.current = false;
        setFieldValue(`location`, {longitude: lng, latitude: lat});
        // Yeni sonuç gelene kadar eski (yanlış) il/ilçe/mahalle kalmasın
        setFieldValue("city", "");
        setFieldValue("county", "");
        setFieldValue("district", "");
        setDistrictId(null);
        setPreferGeocodingForLocation(true);

        const resolved = await resolveAddressFromLocation({lat, lng});
        setFieldValue("city", resolved.city);
        setFieldValue("county", resolved.county);
        setFieldValue("district", resolved.district);
        setDistrictId(resolved.districtId);
        setPreferGeocodingForLocation(!resolved.city);

        if (resolved.matchedLevel === "none") {
            message.warning(
                "Seçilen konum için adres bilgisi alınamadı, il/ilçe/mahalle alanlarını elle seçebilirsiniz."
            );
        } else if (resolved.matchedLevel === "city") {
            message.warning(
                "Konumun ilçesi kayıtlarımızda bulunamadı, ilçe ve mahalleyi elle seçiniz."
            );
        } else if (resolved.matchedLevel === "county") {
            message.warning("Konumun mahallesi kayıtlarımızda bulunamadı, mahalleyi elle seçiniz.");
        }
    };

    // Elle seçilmiş adres varken haritaya tıklanırsa yalnızca pin taşınır; seçim korunur.
    const handleMapClick = ({lat, lng}) => {
        if (!isManualAddressRef.current) {
            handleLocationFromLink({lat, lng});
            return;
        }
        setFieldValue("location", {longitude: lng, latitude: lat});
        setPreviewLocation(null);
        setIsLocationPinned(true);
    };

    const handleFormFinish = () => {
        if (!isLocationPinned) {
            setFieldTouched("location", true);
            message.warning("Lütfen haritadan konum pinleyiniz.");
            return;
        }
        handleSubmit();
    };

    return useObserver(() => (
        <Col data-testid="addSubCompanyScreen">
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleFormFinish}
                data-testid="addSubCompanyForm"
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    <Form.Item
                        label={`${t("fields.companyName", {
                            ns: "common"
                        })} / ${t("fields.branchName", {ns: "common"})}`}
                        validateStatus={
                            getIn(errors, `name`) && getIn(touched, `name`) ? "error" : "success"
                        }
                        help={
                            getIn(errors, `name`) && getIn(touched, `name`)
                                ? getIn(errors, `name`)
                                : null
                        }
                    >
                        <Input
                            name={"name"}
                            onChange={handleChange}
                            value={values.name}
                            data-testid="nameInpuy"
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.authorizedPerson", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? getIn(errors, `authorizedPerson`)
                                : null
                        }
                    >
                        <Input
                            name={"authorizedPerson"}
                            data-testid="authorizedPerson"
                            value={values.authorizedPerson}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedPhone", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? getIn(errors, `authorizedPhone`)
                                        : null
                                }
                            >
                                <MaskedInput
                                    placeholder="0(555)-555-5555"
                                    mask="1(111)-111-1111"
                                    name={"authorizedPhone"}
                                    data-testid="authorizedPhone"
                                    value={values.authorizedPhone}
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedEmail", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? getIn(errors, `authorizedEmail`)
                                        : null
                                }
                            >
                                <Input
                                    name={"authorizedEmail"}
                                    data-testid="authorizedEmail"
                                    onChange={handleChange}
                                    value={values.authorizedEmail}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.address", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? getIn(errors, `address`)
                                : null
                        }
                    >
                        <Input.TextArea
                            name={`address`}
                            data-testid="address"
                            value={values.address}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Form.Item label={t("fields.uniqueKey", {ns: "common"})}>
                        <Input
                            name={"uniqueKey"}
                            data-testid="uniqueKey"
                            onChange={handleChange}
                            value={values.uniqueKey}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Yönetici Firma"
                        validateStatus={
                            getIn(errors, `managerCompany`) && getIn(touched, `managerCompany`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `managerCompany`) && getIn(touched, `managerCompany`)
                                ? getIn(errors, `managerCompany`)
                                : null
                        }
                    >
                        <Select
                            data-testid="managerCompanySelect"
                            placeholder="Seçiniz"
                            value={values.managerCompany}
                            onChange={value => setFieldValue("managerCompany", value)}
                            onBlur={() => setFieldTouched("managerCompany", true)}
                        >
                            {managerCompanyOptions.map(item => (
                                <Select.Option key={item.value} value={item.value}>
                                    {item.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Kredi Modu">
                        <CreditModeSelect
                            showTitle={false}
                            marginBottom="0px"
                            allowClear
                            value={values.creditMode}
                            onChange={handleCreditModeChange}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Fatura Kesim Dönemi"
                        required={isInvoicePeriodRequired(values.creditMode)}
                        validateStatus={
                            getIn(errors, `invoicePeriod`) &&
                            (getIn(touched, `invoicePeriod`) || submitCount > 0)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `invoicePeriod`) &&
                            (getIn(touched, `invoicePeriod`) || submitCount > 0)
                                ? getIn(errors, `invoicePeriod`)
                                : null
                        }
                    >
                        {/* ErrorFocus hataya [name] ile kaydırıyor; antd Select DOM'a name basmıyor */}
                        <Tooltip
                            title={
                                isInvoicePeriodDisabled(values.creditMode)
                                    ? "Kredi modu kredili seçildiğinde fatura dönemi seçimi yapılamamaktadır."
                                    : ""
                            }
                        >
                            <div name="invoicePeriod">
                                <Select
                                    data-testid="invoiceCutPeriodSelect"
                                    placeholder="Seçiniz"
                                    allowClear
                                    disabled={isInvoicePeriodDisabled(values.creditMode)}
                                    value={values.invoicePeriod}
                                    onChange={value =>
                                        setFieldValue("invoicePeriod", value ?? null)
                                    }
                                    onBlur={() => setFieldTouched("invoicePeriod", true)}
                                >
                                    {invoiceCutPeriodOptions.map(item => (
                                        <Select.Option key={item.value} value={item.value}>
                                            {item.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </Tooltip>
                    </Form.Item>
                    <Form.Item label={t("fields.testUser", {ns: "common"})}>
                        <Switch onChange={setIsTestUser} checked={isTestUser} />
                    </Form.Item>
                    <Form.Item label="En Eski Siparişi Alma Zorunluluğu">
                        <Switch
                            data-testid="jokerLeadOrderSwitch"
                            checked={jokerLeadIsActive}
                            onChange={setJokerLeadIsActive}
                        />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                                Süre (dk){" "}
                                <Tooltip
                                    title={
                                        '"En Eski Sipariş Seçme Zorunluluğu" ayarı aktif olduğunda kullanılır. Kuryenin seçmesi zorunlu olan en eski siparişin minimum bekleme süresini belirtir.'
                                    }
                                >
                                    <InfoCircleOutlined style={{color: "#8c8c8c"}} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Input
                            type="number"
                            data-testid="jokerLeadOrderThreshold"
                            min={0}
                            disabled={!jokerLeadIsActive}
                            value={jokerLeadThreshold}
                            onChange={e => {
                                const raw = e.target.value;
                                if (raw === "") {
                                    setJokerLeadThreshold("");
                                    return;
                                }
                                const value = Number(raw);
                                setJokerLeadThreshold(value < 0 ? 0 : value);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t(`integrations.commonFields.allowedOrderProviders`, {
                            ns: "integrations"
                        })}
                    >
                        <TagInput
                            tags={tags}
                            setTags={setTags}
                            addTitle={t(`integrations.commonFields.addAllowedOrderProvider`, {
                                ns: "integrations"
                            })}
                            addPlaceHolderText={t(
                                `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                {
                                    ns: "integrations"
                                }
                            )}
                        ></TagInput>
                    </Form.Item>
                    <Form.Item
                        label={t("fields.location", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? getIn(errors, `location`)
                                : null
                        }
                    >
                        <Row type="flex" gutter={16} justify="center">
                            <Col data-testid="latitudeText">{`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.location.latitude.toFixed(5)}`}</Col>
                            <Col data-testid="longitudeText">{`${t("fields.longitude", {
                                ns: "common"
                            })}: ${values.location.longitude.toFixed(5)}`}</Col>
                        </Row>
                        <SingleMarkerMap
                            center={{
                                lat: values.location.latitude,
                                lng: values.location.longitude
                            }}
                            onClick={handleMapClick}
                            onExternalLink={handleLocationFromLink}
                            previewCenter={previewLocation}
                            previewZoom={previewLocation?.zoom}
                            height="400px"
                            onRecenter={() => setPreviewLocation(null)}
                            addExternalInput
                            icon={<BankOutlined />}
                        />
                    </Form.Item>
                    <Row>
                        <Col span={24}>
                            <LocationFieldsHint>
                                <InfoCircleOutlined className="hint-icon" />
                                <span>
                                    {isResolving
                                        ? "Adres bilgileri alınıyor..."
                                        : "Haritadan konum seçildiğinde il, ilçe ve mahalle bilgileri otomatik olarak alınacak. İl, ilçe ve mahalle elle seçildiğinde harita yalnızca o bölgeye yakınlaşır, konum değişmez; konumu haritaya tıklayarak pinleyebilir veya sağ alttaki butonla pinli konuma dönebilirsiniz."}
                                </span>
                            </LocationFieldsHint>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.city", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `city`) && getIn(touched, `city`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `city`) && getIn(touched, `city`)
                                        ? getIn(errors, `city`)
                                        : null
                                }
                            >
                                <CityFilter
                                    showAllOption={false}
                                    showLabel={false}
                                    value={values.city}
                                    allowClear
                                    onChange={v => {
                                        allowForwardGeocodeRef.current = true;
                                        isManualAddressRef.current = true;
                                        setPreferGeocodingForLocation(false);
                                        setFieldValue("city", v);
                                        setFieldValue("county", "");
                                        setFieldValue("district", "");
                                        setDistrictId(null);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.county", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `county`) && getIn(touched, `county`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `county`) && getIn(touched, `county`)
                                        ? getIn(errors, `county`)
                                        : null
                                }
                            >
                                <DistrictFilter
                                    showAllOption={false}
                                    showLabel={false}
                                    showSearch
                                    allowClear
                                    value={values.county}
                                    onChange={async v => {
                                        allowForwardGeocodeRef.current = true;
                                        isManualAddressRef.current = true;
                                        setFieldValue("county", v);
                                        setFieldValue("district", "");
                                        const foundDistrictId = await resolveDistrictId({
                                            city: values.city,
                                            county: v
                                        });
                                        setDistrictId(foundDistrictId);
                                    }}
                                    disabled={!values?.city}
                                    city={values?.city}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.neighborhood", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `district`) && getIn(touched, `district`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `district`) && getIn(touched, `district`)
                                ? getIn(errors, `district`)
                                : null
                        }
                    >
                        <DynamicNeighborhoodFilter
                            showLabel={false}
                            districtId={districtId}
                            valueKey="name"
                            value={values.district}
                            onChange={v => {
                                allowForwardGeocodeRef.current = true;
                                isManualAddressRef.current = true;
                                setFieldValue("district", v);
                            }}
                            disabled={!districtId}
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.subCompanyCode", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? getIn(errors, `subCompanyCode`)
                                : null
                        }
                    >
                        <Row>
                            <Input
                                disabled
                                value={`${companyCode}`}
                                style={{borderRightWidth: 0, width: 60}}
                            />
                            <Input
                                disabled
                                value={`${companyCityCode}`}
                                style={{borderRightWidth: 0, width: 40}}
                            />
                            <Input
                                disabled
                                name={"subCompanyCode"}
                                data-testid="subCompanyCode"
                                value={values.subCompanyCode}
                                onChange={handleChange}
                                style={{borderLeftWidth: 0, flex: 1}}
                            />
                        </Row>
                    </Form.Item>
                </FormikProvider>
                <Row type="flex">
                    <CustomButton
                        type="primary"
                        htmlType="submit"
                        data-testid="submitButton"
                        disabled={isSubmitting}
                    >
                        {t("actions.save", {ns: "common"})}
                    </CustomButton>
                </Row>
            </Form>
        </Col>
    ));
};
